import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toTitleCase } from "@/lib/utils";
import {
  Search, ShoppingBag, Loader2, ChevronRight, ArrowLeft,
  Microscope, FlaskConical, HeartPulse, Droplet, Droplets, TestTube, TestTubes, Shield, Activity, FileText, Atom
} from "lucide-react";

// Per-segment icon + colour palette (Erba teal accents)
const SEGMENT_META = {
  "Clinical Chemistry":   { Icon: FlaskConical, bg: "bg-[#E0F4F5]", color: "text-[#008A99]", key: "segment_clinical_chemistry" },
  "Critical Care":        { Icon: HeartPulse,   bg: "bg-[#E0F4F5]", color: "text-[#008A99]", key: "segment_critical_care" },
  "Diabetes Management":  { Icon: Droplet,      bg: "bg-[#E0F4F5]", color: "text-[#008A99]", key: "segment_diabetes_management" },
  "Hematology":           { Icon: Droplets,     bg: "bg-[#E0F4F5]", color: "text-[#008A99]", key: "segment_hematology" },
  "ESR":                  { Icon: Activity,     bg: "bg-[#E0F4F5]", color: "text-[#008A99]", key: "segment_esr" },
  "Hemostasis":           { Icon: Microscope,   bg: "bg-[#E0F4F5]", color: "text-[#008A99]", key: "segment_hemostasis" },
  "Immunology":           { Icon: Shield,       bg: "bg-[#E0F4F5]", color: "text-[#008A99]", key: "segment_immunology" },
  "Urinalysis":           { Icon: TestTube,     bg: "bg-[#E0F4F5]", color: "text-[#008A99]", key: "segment_urinalysis" },
  "Molecular":            { Icon: Atom,         bg: "bg-[#E0F4F5]", color: "text-[#008A99]", key: "segment_molecular" },
};
const segmentMeta = (name) => SEGMENT_META[name] || { Icon: FlaskConical, bg: "bg-[#E0F4F5]", color: "text-[#008A99]", key: "" };

export default function ProductCatalog() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

  const [tab, setTab] = useState("equipment");
  const [segments, setSegments] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [reagentTypes, setReagentTypes] = useState([]); // [{value, label}]
  const [products, setProducts] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState("");
  const [selectedInstrument, setSelectedInstrument] = useState("");
  const [selectedReagentType, setSelectedReagentType] = useState("");
  const [step, setStep] = useState("segments"); // segments | instruments | rtype | reagents
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [instImages, setInstImages] = useState({});
  const [assets, setAssets] = useState({});
  const [segmentInstruments, setSegmentInstruments] = useState({}); // segment -> [codes]
  const [instrumentDetail, setInstrumentDetail] = useState(null); // for brochure download

  // Pre-fill from URL ?search= or ?q=
  useEffect(() => {
    const q = searchParams.get("search") || searchParams.get("q") || "";
    const seg = searchParams.get("segment") || "";
    const inst = searchParams.get("instrument") || "";
    if (q) {
      setSearch(q);
      setTab("reagents");
      setStep("reagents");
      setPage(1);
    } else if (inst) {
      setTab("reagents");
      setSelectedInstrument(inst);
      setStep("reagents");
    } else if (seg) {
      setTab("reagents");
      setSelectedSegment(seg);
      // Only Clinical Chemistry & Immunology branch into reagent type selection
      setStep(["Clinical Chemistry", "Immunology"].includes(seg) ? "rtype" : "instruments");
    }
  }, [searchParams]);

  useEffect(() => {
    API.get("/instruments/segments").then(r => setSegments(r.data || [])).catch(() => {});
    API.get("/instruments/images").then(r => setInstImages(r.data || {})).catch(() => {});
    API.get("/assets").then(r => {
      const map = {};
      (r.data || []).forEach(a => { map[a.key] = a; });
      setAssets(map);
    }).catch(() => {});
  }, []);

  // Preload instrument codes per segment (for subtitle preview on segment cards)
  useEffect(() => {
    if (!segments.length) return;
    segments.forEach(async (seg) => {
      try {
        const r = await API.get(`/instruments/by-segment?segment=${encodeURIComponent(seg)}`);
        setSegmentInstruments(prev => ({ ...prev, [seg]: (r.data || []).map(i => i.instrument_code) }));
      } catch {}
    });
  }, [segments]);

  // Load instruments when stepping into instruments
  useEffect(() => {
    if (selectedSegment && step === "instruments") {
      setLoading(true);
      // Reagents tab: only show instruments that have products of selected reagent type
      const url = (tab === "reagents" && selectedReagentType)
        ? `/instruments/by-segment-and-type?segment=${encodeURIComponent(selectedSegment)}&reagent_type=${encodeURIComponent(selectedReagentType)}`
        : `/instruments/by-segment?segment=${encodeURIComponent(selectedSegment)}`;
      API.get(url)
        .then(r => setInstruments(r.data || []))
        .catch(() => {}).finally(() => setLoading(false));
    }
  }, [selectedSegment, step, tab, selectedReagentType]);

  // Load reagent types when entering rtype step
  useEffect(() => {
    if (selectedSegment && step === "rtype" && tab === "reagents") {
      API.get(`/reagent-types/${encodeURIComponent(selectedSegment)}`)
        .then(r => setReagentTypes(r.data || []))
        .catch(() => setReagentTypes([]));
    }
  }, [selectedSegment, step, tab]);

  // Load instrument detail (for brochure) when picking an instrument
  useEffect(() => {
    if (selectedInstrument) {
      API.get(`/instruments/${encodeURIComponent(selectedInstrument)}`)
        .then(r => setInstrumentDetail(r.data || null))
        .catch(() => setInstrumentDetail(null));
    } else {
      setInstrumentDetail(null);
    }
  }, [selectedInstrument]);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 60 });
      if (selectedSegment) params.set("category", selectedSegment);
      if (selectedInstrument) params.set("instrument_code", selectedInstrument);
      if (selectedReagentType) params.set("reagent_type", selectedReagentType);
      if (search) params.set("search", search);
      const { data } = await API.get(`/products?${params}`);
      const sorted = (data.products || []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
      setProducts(sorted);
      setTotalPages(data.pages || 1);
    } catch {} finally { setLoading(false); }
  }, [selectedSegment, selectedInstrument, selectedReagentType, search, page]);

  useEffect(() => { if (step === "reagents" || search) loadProducts(); }, [step, search, loadProducts]);

  // Segments that branch further into reagent-type selection on the Reagents tab.
  // All other segments skip rtype and go directly: Segment → Instrument → Reagents.
  const RTYPE_SEGMENTS = ["Clinical Chemistry", "Immunology"];

  // Step transitions
  const selectSegment = (seg) => {
    setSelectedSegment(seg);
    if (tab === "equipment") {
      setStep("instruments");
    } else if (RTYPE_SEGMENTS.includes(seg)) {
      setStep("rtype");                  // Reagents tab + CC/Immunology: Segment → ReagentType → Instrument → Reagents
    } else {
      setSelectedReagentType("");        // Other segments: skip rtype, jump to instruments directly
      setStep("instruments");
    }
    setPage(1);
  };
  const selectInstrument = (inst) => {
    setSelectedInstrument(inst);
    if (tab === "reagents") {
      setStep("reagents");
      setPage(1);
    } else {
      // Instruments tab → show instrument detail (with brochure download)
      setStep("instrument-detail");
    }
  };
  const selectReagentType = (rt) => {
    setSelectedReagentType(rt);
    // Rapids has no instrument mapping — jump straight to the reagents list
    if (rt === "rapids") {
      setSelectedInstrument("");
      setStep("reagents");
    } else {
      setStep("instruments");              // After reagent type → pick instrument
    }
    setPage(1);
  };
  const goBack = () => {
    if (step === "reagents") {
      // Rapids skips the instrument step; jump back to reagent-type selection
      if (selectedReagentType === "rapids") {
        setStep("rtype"); setSelectedReagentType(""); setSelectedInstrument(""); setProducts([]);
      } else {
        setStep("instruments"); setSelectedInstrument(""); setProducts([]);
      }
    } else if (step === "instruments") {
      if (tab === "reagents") {
        if (RTYPE_SEGMENTS.includes(selectedSegment)) {
          setStep("rtype"); setSelectedReagentType(""); setInstruments([]);
        } else {
          setStep("segments"); setSelectedSegment(""); setInstruments([]);
        }
      }
      else { setStep("segments"); setSelectedSegment(""); setInstruments([]); }
    } else if (step === "rtype") {
      setStep("segments"); setSelectedSegment(""); setSelectedReagentType("");
    } else if (step === "instrument-detail") {
      setStep("instruments"); setSelectedInstrument("");
    }
  };
  const switchTab = (t) => {
    setTab(t); setStep("segments");
    setSelectedSegment(""); setSelectedInstrument(""); setSelectedReagentType("");
    setInstruments([]); setProducts([]); setSearch("");
  };

  const breadcrumb = [selectedSegment, selectedInstrument, selectedReagentType].filter(Boolean).map(toTitleCase).join(" / ");

  return (
    <div className="space-y-4" data-testid="product-catalog">
      <div className="flex items-center gap-3">
        {step !== "segments" && <button onClick={goBack} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="catalog-back-btn"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>}
        <div>
          <h2 className="text-xl font-bold text-gray-900">Products</h2>
          <p className="text-xs text-gray-500">{breadcrumb || "Browse instruments & reagents"}</p>
        </div>
      </div>

      <div className="flex gap-2" data-testid="product-tabs">
        {(() => {
          const instAsset = assets["icon_instrument"];
          const rxAsset = assets["icon_reagent"];
          const instLogo = instAsset?.image_url ? (instAsset.image_url.startsWith("http") ? instAsset.image_url : `${BACKEND}${instAsset.image_url}`) : "";
          const rxLogo = rxAsset?.image_url ? (rxAsset.image_url.startsWith("http") ? rxAsset.image_url : `${BACKEND}${rxAsset.image_url}`) : "";
          return (
            <>
              <button onClick={() => switchTab("equipment")} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-colors ${tab === "equipment" ? "bg-[#008A99] text-white" : "bg-gray-100 text-gray-600"}`} data-testid="equipment-tab">
                {instLogo ? <img src={instLogo} alt="" className="w-5 h-5 object-contain" /> : <Microscope className="w-4 h-4" />} Instruments
              </button>
              <button onClick={() => switchTab("reagents")} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-colors ${tab === "reagents" ? "bg-[#008A99] text-white" : "bg-gray-100 text-gray-600"}`} data-testid="reagents-tab">
                {rxLogo ? <img src={rxLogo} alt="" className="w-5 h-5 object-contain" /> : <TestTubes className="w-4 h-4" />} Reagents
              </button>
            </>
          );
        })()}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input type="text" value={search} onChange={e => { setSearch(e.target.value); if (e.target.value) setStep("reagents"); }}
          placeholder={`Search ${tab === "equipment" ? "instruments" : "reagents"}...`}
          className="w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:border-[#008A99]" data-testid="product-search" />
      </div>

      {/* Step 1 — Segments */}
      {step === "segments" && !search && (
        <div className="space-y-2" data-testid="segment-step">
          <p className="text-sm font-semibold text-gray-700">Select Segment</p>
          {segments.map(s => {
            const meta = segmentMeta(s);
            const SegIcon = meta.Icon;
            const asset = meta.key ? assets[meta.key] : null;
            const logoUrl = asset?.image_url ? (asset.image_url.startsWith("http") ? asset.image_url : `${BACKEND}${asset.image_url}`) : "";
            const codes = segmentInstruments[s] || [];
            const codeSummary = codes.length === 0 ? "" : codes.slice(0, 3).join(", ") + (codes.length > 3 ? ` +${codes.length - 3} more` : "");
            return (
              <button key={s} onClick={() => selectSegment(s)} className="w-full flex items-center justify-between p-4 bg-white rounded-xl border hover:border-[#008A99] hover:shadow-sm transition-all" data-testid={`segment-${s}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center overflow-hidden bg-transparent">
                    {logoUrl ? <img src={logoUrl} alt={s} className="w-full h-full object-contain p-1.5" /> : <SegIcon className={`w-5 h-5 ${meta.color}`} />}
                  </div>
                  <div className="text-left min-w-0">
                    <span className="font-semibold text-sm text-gray-900 block truncate">{toTitleCase(s)}</span>
                    {codeSummary && <span className="text-[10px] text-gray-500 font-mono block truncate" data-testid={`segment-codes-${s}`}>{codeSummary}</span>}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      {/* Step 2 — Instruments */}
      {step === "instruments" && !search && (
        <div className="space-y-2" data-testid="instrument-step">
          <p className="text-sm font-semibold text-gray-700">
            {tab === "reagents" && selectedReagentType
              ? `Select Instrument — ${reagentTypes.find(rt => rt.value === selectedReagentType)?.label || selectedReagentType}`
              : "Select Instrument"}
          </p>
          {loading ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-[#008A99] mx-auto" /></div>
            : instruments.length === 0 ? (
              tab === "reagents" ? (
                <div className="space-y-2">
                  <p className="text-center text-gray-400 py-4 text-sm">No instrument mapping for this reagent type. You can still view all reagents below.</p>
                  <button onClick={() => { setSelectedInstrument(""); setStep("reagents"); setPage(1); }}
                    className="w-full flex items-center justify-between p-4 bg-white rounded-xl border hover:border-[#008A99] hover:shadow-sm transition-all"
                    data-testid="view-all-reagents-of-type">
                    <div className="flex items-center gap-2.5">
                      <TestTubes className="w-4 h-4 text-[#008A99]" />
                      <span className="font-semibold text-sm text-gray-900">View all {reagentTypes.find(rt => rt.value === selectedReagentType)?.label || "Reagents"}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              ) : <p className="text-center text-gray-400 py-8 text-sm">No instruments in this segment yet.</p>
            )
            : <div className="grid grid-cols-2 gap-3" data-testid="instrument-grid">{instruments.map((inst, i) => {
              const sys = inst.system_type;
              const cfg = sys === "open" ? { label: "Open System", iconClr: "text-emerald-600" }
                : sys === "close" ? { label: "Closed System", iconClr: "text-sky-600" }
                : { label: "Open & Closed", iconClr: "text-amber-600" };
              const photoFromAdmin = inst.image_url ? `${BACKEND}${inst.image_url}` : "";
              const photoFromBundle = instImages[(inst.instrument_code || "").replace(/\s/g, '').toUpperCase()];
              const photo = photoFromAdmin || (photoFromBundle ? `${BACKEND}${photoFromBundle}` : "");
              return (
                <button key={i} onClick={() => selectInstrument(inst.instrument_code)}
                  className="flex flex-col items-center text-center gap-2 p-4 bg-white rounded-xl border hover:border-[#008A99] hover:shadow-md transition-all" data-testid={`instrument-${inst.instrument_code}`}>
                  <div className="w-16 h-16 rounded-lg flex items-center justify-center overflow-hidden bg-[#E0F4F5]/40">
                    {photo ? (
                      <img src={photo} alt={inst.instrument_code} className="w-full h-full object-contain" />
                    ) : (
                      <Microscope className={`w-7 h-7 ${cfg.iconClr}`} />
                    )}
                  </div>
                  <span className="font-semibold text-xs text-gray-900 leading-tight line-clamp-2">{inst.instrument_code}</span>
                </button>
              );
            })}</div>}
        </div>
      )}

      {/* Step 3 — Reagent Type (only for Clinical Chemistry under reagents tab) */}
      {step === "rtype" && tab === "reagents" && !search && (
        <div className="space-y-3" data-testid="rtype-step">
          <p className="text-sm font-semibold text-gray-700">Select Reagent Type</p>
          {reagentTypes.length === 0 && <p className="text-center text-gray-400 py-4 text-sm">No reagent types available yet.</p>}
          {reagentTypes.map(rt => (
            <button key={rt.value} onClick={() => selectReagentType(rt.value)}
              className="w-full flex items-center justify-between p-4 bg-white rounded-xl border hover:border-[#008A99] hover:shadow-sm transition-all" data-testid={`rtype-${rt.value}`}>
              <span className="font-semibold text-sm text-gray-900">{rt.label}</span>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </button>
          ))}
        </div>
      )}

      {/* Step — Instrument Detail (Instruments tab only): photo + brochure download */}
      {step === "instrument-detail" && tab === "equipment" && (
        <div className="space-y-3" data-testid="instrument-detail-step">
          {(() => {
            const code = selectedInstrument;
            const photoFromAdmin = instrumentDetail?.image_url ? `${BACKEND}${instrumentDetail.image_url}` : "";
            const photoFromBundle = instImages[(code || "").replace(/\s/g, '').toUpperCase()];
            const photo = photoFromAdmin || (photoFromBundle ? `${BACKEND}${photoFromBundle}` : "");
            return (
              <div className="bg-white rounded-2xl border overflow-hidden">
                <div className="aspect-video bg-gradient-to-br from-[#E0F4F5] to-[#F0F9FF] flex items-center justify-center">
                  {photo ? (
                    <img src={photo} alt={code} className="w-full h-full object-contain" />
                  ) : (
                    <Microscope className="w-16 h-16 text-[#008A99]/30" />
                  )}
                </div>
                <div className="p-4 space-y-1">
                  <p className="text-base font-bold text-gray-900">{toTitleCase(instrumentDetail?.name || code)}</p>
                  {instrumentDetail?.tagline && (
                    <p className="text-sm font-semibold text-[#008A99] italic leading-snug" data-testid="instrument-tagline">{instrumentDetail.tagline}</p>
                  )}
                  {instrumentDetail?.description && (
                    <div className="mt-3 pt-3 border-t border-gray-100" data-testid="instrument-description">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Description</p>
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{instrumentDetail.description}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {instrumentDetail?.brochure_url ? (
            <a href={`${BACKEND}${instrumentDetail.brochure_url}`} target="_blank" rel="noopener noreferrer"
               className="flex items-center justify-between gap-3 px-4 py-3 bg-[#008A99]/5 border border-[#008A99]/30 rounded-xl hover:bg-[#008A99]/10 transition-colors"
               data-testid={`download-brochure-${selectedInstrument}`}>
              <div className="flex items-center gap-2.5">
                <FileText className="w-5 h-5 text-[#008A99]" />
                <div>
                  <p className="text-xs font-bold text-[#008A99]">Download Brochure</p>
                  <p className="text-[10px] text-gray-500">{instrumentDetail.brochure_filename || `${selectedInstrument}.pdf`}</p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-[#008A99]" />
            </a>
          ) : (
            <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-500 flex items-center gap-2" data-testid="no-brochure-msg">
              <FileText className="w-4 h-4 text-gray-400" />
              Brochure not available for this instrument yet.
            </div>
          )}

          <button
            onClick={() => { setTab("reagents"); setSelectedReagentType(""); setStep("reagents"); setPage(1); }}
            className="w-full flex items-center justify-between p-4 bg-white rounded-xl border hover:border-[#008A99] hover:shadow-sm transition-all"
            data-testid={`view-reagents-for-${selectedInstrument}`}
          >
            <div className="flex items-center gap-2.5">
              <TestTubes className="w-4 h-4 text-[#008A99]" />
              <span className="font-semibold text-sm text-gray-900">View Reagents for {selectedInstrument}</span>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      )}

      {/* Step 4 — Reagents */}
      {(step === "reagents" || search) && (
        <div data-testid="reagents-step">
          {(() => {
            const rxAsset = assets["icon_reagent"];
            const rxLogo = rxAsset?.image_url ? (rxAsset.image_url.startsWith("http") ? rxAsset.image_url : `${BACKEND}${rxAsset.image_url}`) : "";
            return loading ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#008A99] mx-auto" /></div> :
            products.length === 0 ? <div className="text-center py-12"><ShoppingBag className="w-10 h-10 text-gray-200 mx-auto mb-3" /><p className="text-sm text-gray-400">No reagents available for this selection yet.</p></div> :
            <div className="grid grid-cols-2 gap-3" data-testid="reagent-grid">
              {products.map(p => (
                <button key={`${p.product_code}-${p.instrument_code}-${p.reagent_type}`} onClick={() => navigate(`/app/products/${p.product_code}`)}
                  className="bg-white rounded-xl border shadow-sm p-3 text-left transition-all hover:shadow-md hover:border-[#008A99]/30 flex flex-col gap-2"
                  data-testid={`reagent-${p.product_code}`}>
                  <div className="w-full aspect-square rounded-lg bg-[#E0F4F5]/40 flex items-center justify-center overflow-hidden">
                    {p.image_url ? (
                      <img src={p.image_url.startsWith("http") ? p.image_url : `${BACKEND}${p.image_url}`} alt={p.name} className="w-full h-full object-contain" />
                    ) : rxLogo ? (
                      <img src={rxLogo} alt="reagent" className="w-12 h-12 object-contain opacity-70" />
                    ) : (
                      <FlaskConical className="w-8 h-8 text-[#008A99]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xs font-semibold text-gray-900 line-clamp-2 leading-snug">{toTitleCase(p.name)}</h3>
                    <div className="flex items-center gap-2 mt-1 flex-wrap" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", Tahoma, sans-serif' }}>
                      <span className="text-[10px] text-gray-400">Code: <span className="font-semibold">{p.product_code}</span></span>
                      {p.pack_size && <span className="text-[10px] text-gray-400">Pack: {p.pack_size}</span>}
                    </div>
                  </div>
                </button>
              ))}
            </div>;
          })()}
          {totalPages > 1 && <div className="flex justify-center gap-2 mt-4"><button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page===1} className="px-3 py-1.5 text-xs border rounded-lg disabled:opacity-30">Prev</button><span className="text-xs text-gray-500 py-1.5">{page}/{totalPages}</span><button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page===totalPages} className="px-3 py-1.5 text-xs border rounded-lg disabled:opacity-30">Next</button></div>}
        </div>
      )}
    </div>
  );
}
